import React from 'react';
import { useIntl } from 'react-intl';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';

import { Formik, Form, Field } from 'formik';
import PropTypes from 'prop-types';

import AutoSavingForm from '@/components/FixAddress/AutoSavingForm';
import normalizeText from '@/components/FixAddress/normalizeText';
import { cn } from '@/lib/utils';

const selectStyles = (hasError) => ({
  control: (base, state) => ({
    ...base,
    borderColor: hasError ? '#ff0000' : state.isFocused ? '#80bdff' : '#ced4da',
    '&:hover': {
      borderColor: hasError ? '#ff0000' : state.isFocused ? '#80bdff' : '#ced4da',
    },
  }),
});

const FixAddressComponent = ({
  initialValues,
  address,
  states,
  cities,
  neighborhoods,
  predictions,
  validationSchema,
  onCityChange,
  onNeighborhoodChange,
  onStateChange,
  onSubmit,
  selectPrediction,
  addressValidation: { city = false, state = false, neighborhood = false },
  formikRef,
}) => {
  const intl = useIntl();

  const normalizedNeighborhood = normalizeText(address?.neighborhood);

  const neighborhoodOptions =
    normalizedNeighborhood && !neighborhoods.some(n => n.label?.toLowerCase() === normalizedNeighborhood.toLowerCase())
      ? [...neighborhoods, { label: normalizedNeighborhood, value: null }]
      : neighborhoods;

  const selectedNeighborhood =
    neighborhoodOptions.find(n => n.label?.toLowerCase() === normalizedNeighborhood?.toLowerCase()) || null;

  return (
    <div className="w-full">
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        validateOnMount
        innerRef={formikRef}
      >
        {({ values, errors }) => (
          <Form>
            <div className="flex" style={{ marginTop: '16px' }}>
              <p className="text-left text-[#c4c4c4] text-[10.8px] leading-[16.2px]" style={{ paddingTop: '8px', marginBottom: '16px' }}>
                {intl.formatMessage({ id: 'FIX_ADDRESS.CORRECTION_INFO' })}
              </p>
            </div>
            
            <p className="text-sm text-left text-[#303030] leading-[21px]" style={{ marginBottom: '16px' }}>
              {intl.formatMessage({ id: 'FIX_ADDRESS.RECEIVED_ADDRESS' })}&nbsp;
              <strong>{address.neighborhood}</strong>
              ,&nbsp;
              <strong>{address.city}</strong>/<strong>{address.state}</strong>
            </p>

            <div className="flex" style={{ marginTop: '16px' }}>
              <div style={{ flex: '0 0 20%', minWidth: '110px' }}>
                <Field name="state">
                  {({ field, form }) => {
                    const stateHasError = errors?.state || (errors?.state && state === 'invalid');
                    return (
                      <Select
                        {...field}
                        name={field.name}
                        id="state"
                        className="react-select-container"
                        classNamePrefix="select"
                        placeholder="UF"
                        onChange={option => onStateChange(option, form)}
                        onBlur={field.onBlur}
                        options={states}
                        value={field.value || null}
                        required
                        noOptionsMessage={() => intl.formatMessage({ id: 'FIX_ADDRESS.NO_STATES_AVAILABLE' })}
                        isClearable
                        styles={selectStyles(stateHasError)}
                      />
                    );
                  }}
                </Field>
                {(errors?.state || (errors?.state && state === 'invalid')) && (
                  <p className="text-[8px] text-[#ff0000] text-left">
                    {intl.formatMessage({ id: 'FIX_ADDRESS.FIELD_ERROR' })}
                  </p>
                )}
              </div>

              <div style={{ flex: 1, marginLeft: '22px' }}>
                <Field name="city">
                  {({ field, form }) => {
                    const cityHasError = errors?.city || (errors?.city && city === 'invalid');
                    return (
                      <Select
                        {...field}
                        name={field.name}
                        id="city"
                        className="react-select-container"
                        classNamePrefix="select"
                        placeholder="Cidade"
                        onChange={option => onCityChange(option, form)}
                        onBlur={field.onBlur}
                        options={cities}
                        value={field.value || null}
                        required
                        noOptionsMessage={() => intl.formatMessage({ id: 'FIX_ADDRESS.NO_CITIES_AVAILABLE' })}
                        isClearable
                        maxMenuHeight={150}
                        styles={selectStyles(cityHasError)}
                      />
                    );
                  }}
                </Field>
                {(errors?.city || (errors?.city && city === 'invalid')) && (
                  <p className="text-[8px] text-[#ff0000] text-left">
                    {intl.formatMessage({ id: 'FIX_ADDRESS.FIELD_ERROR' })}
                  </p>
                )}
              </div>
            </div>

            <div className="flex" style={{ marginTop: '16px' }}>
              <div className="flex-1">
                <Field name="neighborhood">
                  {({ field, form }) => {
                    const neighborhoodHasError =
                      (errors?.neighborhood || (errors?.neighborhood && neighborhood === 'invalid')) &&
                      address.neighborhood === '';
                    return (
                      <CreatableSelect
                        {...field}
                        name={field.name}
                        id="neighborhood"
                        className="react-select-container"
                        classNamePrefix="select"
                        placeholder="Bairro"
                        onChange={option => onNeighborhoodChange(option, form)}
                        onBlur={field.onBlur}
                        options={neighborhoodOptions}
                        value={field.value || null}
                        required
                        noOptionsMessage={() => intl.formatMessage({ id: 'FIX_ADDRESS.NO_NEIGHBORHOODS_AVAILABLE' })}
                        isClearable
                        formatCreateLabel={inputValue =>
                          intl.formatMessage({ id: 'FIX_ADDRESS.ADD_NEIGHBORHOOD' }, { inputValue })
                        }
                        styles={selectStyles(neighborhoodHasError)}
                      />
                    );
                  }}
                </Field>
                {(errors?.neighborhood || (errors?.neighborhood && neighborhood === 'invalid')) &&
                address.neighborhood === '' && (
                  <p className="text-[8px] text-[#ff0000] text-left">
                    {intl.formatMessage({ id: 'FIX_ADDRESS.FIELD_ERROR' })}
                  </p>
                )}
              </div>
            </div>

            {predictions && predictions.length > 0 ? (
              <div className="flex" style={{ marginTop: '16px' }}>
                <div className="flex-wrap list-none text-sm text-left">
                  {intl.formatMessage({ id: 'FIX_ADDRESS.SUGGESTIONS' })}&nbsp;
                  {predictions.map((prediction, index) => (
                    <span className="contents px-1.5" key={prediction?.neighborhood?.id}>
                      {index > 0 && <span className="text-[#212529] mx-1">•</span>}
                      <button
                        type="button"
                        onClick={() => selectPrediction(prediction)}
                        className="contents ml-1 p-0 border-0 bg-transparent break-all text-[#4c8fca] hover:text-[#6da2d5] cursor-pointer"
                      >
                        {`${prediction?.neighborhood?.name}, ${prediction.city.name} - ${prediction.state.name}`}
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
            
            <AutoSavingForm values={values} />
          </Form>
        )}
      </Formik>
    </div>
  );
};

FixAddressComponent.propTypes = {
  initialValues: PropTypes.object.isRequired,
  address: PropTypes.object.isRequired,
  states: PropTypes.array,
  cities: PropTypes.array,
  predictions: PropTypes.array,
  neighborhoods: PropTypes.array,
  addressValidation: PropTypes.object,
  validationSchema: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onStateChange: PropTypes.func.isRequired,
  onCityChange: PropTypes.func.isRequired,
  onNeighborhoodChange: PropTypes.func.isRequired,
  selectPrediction: PropTypes.func.isRequired,
  formikRef: PropTypes.object.isRequired,
};

FixAddressComponent.defaultProps = {
  states: [],
  cities: [],
  neighborhoods: [],
  predictions: null,
  addressValidation: {},
};

export default FixAddressComponent;
